import type { Mutate, StateCreator, StoreApi } from 'zustand';

import { createStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import getSessionKey from '@core/utils/getSessionKey';

import { devtoolsOptions } from '.';
/**
 * The key for the property that contains all of our cross-frame stores
 * in the window.
 */
const globalStoreMap = `@readmeStore-${getSessionKey()}`;

/**
 * Create a new Zustand store that is shared between the main app and the
 * preview iframe in SuperHub.
 *
 * @example
 * const myStore = createCrossFrameStore('my-store', (set, get) => ({
 *  count: 0,
 *  setCount: (count: number) => {...}
 * });
 */
export const createCrossFrameStore = <T>(
  name: string,
  initializer: StateCreator<
    T,
    [['zustand/devtools', never], ['zustand/immer', never]],
    [['zustand/persist', unknown]] | []
  >,
): Mutate<StoreApi<T>, [['zustand/devtools', never], ['zustand/immer', never]]> => {
  const isClient = typeof window !== 'undefined';
  const isParent = isClient && window.self === window.top;

  // https://bin.readme.com/ will load an iframe with the Hub app from 'preview.readme.io'
  // In this case we don't want to share the store across frames.
  const isBinPreviewSite = process.env.NODE_ENV === 'preview';

  const storeCreator = () => createStore<T>()(devtools(immer(initializer), devtoolsOptions(name)));

  // If we are on a server, return a new store.
  if (!isClient || isBinPreviewSite) {
    return storeCreator();
  }

  // If we are in a parent window, create a new store and tack it to the global
  // `window` object so that it can be accessed by child iframes.
  if (isParent) {
    window[globalStoreMap] ??= {};
    window[globalStoreMap][name] ??= storeCreator();
    return window[globalStoreMap][name];
  }

  // If we are in an iframe, return the store from the parent window.
  // If it doesn't exist, create a new one and store it in the parent window.
  window.parent[globalStoreMap] ??= {};
  window.parent[globalStoreMap][name] ??= storeCreator();
  return window.parent[globalStoreMap][name];
};
